import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  FaCogs,
  FaInfoCircle,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSearch,
  FaUndoAlt,
  FaLongArrowAltRight,
} from "react-icons/fa";
import { useDeviceConsumentReportData } from "../../../Hooks/device/useDeviceConsumentReportData";
import { useDeviceSettingReportData } from "../../../Hooks/device/useDeviceSettingReportData";
import { useDeviceOperationReportData } from "../../../Hooks/device/useDeviceOperationReportData";
import { useDeviceManualQuotaReportData } from "../../../Hooks/device/useDeviceManualQuotaReportData";
import { useDeviceInfoReportData } from "../../../Hooks/device/useDeviceInfoReportData";
import { useDeviceQuotaChangeReportData } from "../../../Hooks/device/useDeviceQuotaChangeReportData";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import ExportExcel from "../../Utils/ExportExcel";
import ConsumentEditDrawer from "./ConsumentEditDrawer";
import ReportTable from "../../Reports/ReportTable";
import PerformanceReportView from "../../Reports/PerformanceReportView";
import useDetailPerformanceReport from "../../../Hooks/useDetailPerformanceReport";
import { creationMinDate, clampToCreation } from "../../../utils/creationInfo";
import { CustomDropdown, DateRangeField } from "../../atoms";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import trLocale from "date-fns/locale/tr";
import moment from "moment-timezone";
import mascotNoData from "../../../Assets/icons/okkali_mascot_nodata.png";
import {
  STATUS_ORDER,
  getStatusLocative,
  getStatusColorClasses,
  sortStatuses,
} from "./reportStatusUtils";
// Operasyon meta anahtarları → Türkçe etiket (tek kaynak, cihaz kartı Özet'i ile ortak)
import {
  getConsumentLabel,
  getMetadataLabel,
  STEP_TYPE_LABELS,
  STEP_KEY_LABELS,
} from "../../../utils/operationMetadata";
import {
  displayLogEntry,
  displayLogInfoName,
  displayLogText,
} from "../../../utils/deviceActivityLabels";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";

// Operasyon adının görünen karşılığı (eski İngilizce log adları çevrilir,
// yeni loglar zaten Türkçe gelir).
// Cihaz kota degisim kaynaklari — server SourceEnum ile birebir
// (modules/device/data/models/deviceQuotaChangeLog.model.js).
// Yeni kaynak eklenirse IKI tarafi da guncelle.
const DEVICE_QUOTA_SOURCE_LABELS = {
  AutoDistribution: "Otomatik Dağıtım",
  ManualQuota: "Manuel Kota",
  ManualTransfer: "Elle Transfer",
  DeviceReturn: "Firmadan Alındı",
  OfflineConsumption: "Çevrimdışı Tüketim",
  OperationReset: "Kota Sıfırlama",
  SettingChange: "Dağıtım Ayarı",
  ProductDelete: "Ürün Silme",
};

const DEVICE_QUOTA_SOURCE_COLORS = {
  AutoDistribution: "bg-blue-100 text-blue-700",
  ManualQuota: "bg-yellow-100 text-yellow-700",
  ManualTransfer: "bg-indigo-100 text-indigo-700",
  DeviceReturn: "bg-teal-100 text-teal-700",
  OfflineConsumption: "bg-orange-100 text-orange-700",
  OperationReset: "bg-rose-100 text-rose-700",
  SettingChange: "bg-purple-100 text-purple-700",
  ProductDelete: "bg-gray-200 text-gray-700",
};

const getOperationDisplayName = (name) =>
  name === "CleaningSave" ? "Temizlik" : name === "BrewLogSave" ? "Demleme" : name || "-";

// Meta bölümü bu operasyon tiplerinde gösterilmez (Temizlik, Demlik kalibrasyonu, Ağırlık Kalibrasyonu)
const OPERATIONS_HIDE_META = ["CleaningSave"];
const isOperationHidingMeta = (operationName) => {
  if (!operationName) return false;
  if (OPERATIONS_HIDE_META.includes(operationName)) return true;
  const lower = operationName.toLowerCase();
  if (lower.includes("potcalibration") || lower.includes("demlikkalibrasyon")) return true;
  if (lower.includes("weightcalibration") || lower.includes("ağırlıkkalibrasyon") || lower.includes("cupcalibration")) return true;
  return false;
};

// Meta gösteriminde gösterilmeyecek anahtarlar (operation key, göz, güncelleme
// bilgisi, tarih alanları — zaman zaten drawer'ın Zaman box'ında gösteriliyor)
const METADATA_HIDDEN_KEYS = ["operationKey", "index", "updatedInfo", "date", "startDate"];

// Meta alanlarının gösterim sırası (özellikle çay/filtre kahve demleme metası için).
// Listede olmayan anahtarlar sona, geldiği sırayla eklenir.
const METADATA_KEY_ORDER = [
  "productName",
  "index",
  "totalCupNumber",
  "consument",
  "consumentQuota",
  "drink",
  "drinkQuota",
  "totalBrewVolume",
  "remainingBrewVolume",
  "brewTime",
  "totalWaterTime",
  "stalePot",
  "program",
  "programDateTime",
  "type",
  "date",
  "startDate",
  "updatedInfo",
];

// İlişkili tüketim alanları tek box içinde çift olarak gösterilir:
// Demlenen/Pişirilen + İçilen, Tüketilen Kota + İçilen Kota, Toplam + Kalan Dem.
const METADATA_PAIR_GROUPS = [
  ["consument", "drink"],
  ["consumentQuota", "drinkQuota"],
  ["totalBrewVolume", "remainingBrewVolume"],
];

const sortMetadataEntries = (entries) => {
  const orderOf = ([key]) => {
    const idx = METADATA_KEY_ORDER.indexOf(key);
    return idx === -1 ? METADATA_KEY_ORDER.length : idx;
  };
  return [...entries].sort((a, b) => orderOf(a) - orderOf(b));
};

const ReportTab = ({ selectedDevice }) => {
  const { auth } = useAuth();
  const { hasPermission, isAdmin } = usePermission();
  // Bayide tüketim durum seçimi yok: sunucu yalnız "Firma" durumundaki ve bayi
  // ağındaki (kendisi + müşterileri) tüketimi döndürür.
  const isDealer = auth?.role === "dealer";
  // Tüketim kaydı düzeltme drawer'ı (sadece admin, saatlik görünümde)
  const [consumentEdit, setConsumentEdit] = useState({ open: false, log: null });
  const token = auth.accessToken;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  // Varsayılan "Tüketim" = Müşteri Performans biçimi (PDF + Excel, #343);
  // eski tablo "Tüketim Detayı" (Consument)
  const [reportType, setReportType] = useState("ConsumentReport");
  const detailReport = useDetailPerformanceReport("device");
  const isDetailReport = reportType === "ConsumentReport";
  // Cihazın oluşturulma gününden önce seçilemez (#343)
  const minReportDate = creationMinDate(selectedDevice?.createdInfo);
  const [filterType, setFilterType] = useState("Day");
  const [parsedData, setParsedData] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(
    selectedDevice?.productInfo?.productInfo?.[0]?.productName || ""
  );
  const [productError, setProductError] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState(["Firma"]);
  const [selectedOperationLog, setSelectedOperationLog] = useState(null);
  const [showOperationDrawer, setShowOperationDrawer] = useState(false);
  // Operasyon drawer'ı da diğerleriyle aynı giriş/çıkış kaymasını kullanır.
  const { mounted: opDrawerMounted, entered: opDrawerEntered } =
    useDrawerTransition(showOperationDrawer);
  // Operasyon Bilgisi raporunda operasyon ismine göre filtre ("all" = tümü)
  const [selectedOperationName, setSelectedOperationName] = useState("all");
  // Bilgi Değişikliği raporunda bilgi adına göre filtre ("all" = tümü)
  const [selectedInfoName, setSelectedInfoName] = useState("all");

  // Rapor tipine göre doğru hook'u kullan
  const consumentReport = useDeviceConsumentReportData(
    selectedDevice?._id,
    token
  );
  const settingReport = useDeviceSettingReportData(selectedDevice?._id, token);
  const operationReport = useDeviceOperationReportData(
    selectedDevice?._id,
    token
  );
  const manualQuotaReport = useDeviceManualQuotaReportData(
    selectedDevice?._id,
    token
  );
  const infoReport = useDeviceInfoReportData(selectedDevice?._id, token);
  const quotaChangeReport = useDeviceQuotaChangeReportData(
    selectedDevice?._id,
    token
  );

  // Aktif rapor tipine göre hook'u seç
  const getActiveReport = () => {
    if (reportType === "Consument") {
      return consumentReport;
    } else if (reportType === "UpdateSetting") {
      return settingReport;
    } else if (reportType === "OperationInfo") {
      return operationReport;
    } else if (reportType === "UpdateManualQuota") {
      return manualQuotaReport;
    } else if (reportType === "UpdateInfo") {
      return infoReport;
    } else if (reportType === "QuotaChange") {
      return quotaChangeReport;
    }
    return consumentReport; // Varsayılan
  };

  const activeReport = getActiveReport();
  const {
    input,
    setInput,
    setDate,
    setDateSecond,
    isLoading,
    error,
    Datas,
    handleGetLogData,
  } = activeReport;

  const getStartOfDay = (date = new Date()) => {
    const today = moment(date).tz(tz).startOf("day");
    return today.toDate();
  };

  const getEndOfDay = (date = new Date()) => {
    const todayEnd = moment(date).tz(tz).endOf("day");
    return todayEnd.toDate();
  };

  const [startDate, setStartDate] = useState(getStartOfDay());
  const [endDate, setEndDate] = useState(getEndOfDay());

  // Varsayılan rapor tipini 'Tüketim' olarak ayarla
  useEffect(() => {
    if (!input.type) {
      setInput({ ...input, type: "Consument" });
    }
  }, [input.type, input, setInput]);

  // Başlangıçta ayın 1'i ve bugünü tarih inputlarına set et
  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const start = clampToCreation(firstDay, selectedDevice?.createdInfo);
    setDate(start);
    setStartDate(start);
    setDateSecond(today);
    setEndDate(today);
  }, [setDate, setDateSecond, selectedDevice?.createdInfo]);

  const allReportTypeOptions = [
    { id: "ConsumentReport", label: "Tüketim", value: "ConsumentReport", permission: "devices.reportConsumption" },
    { id: "Consument", label: "Tüketim Detayı", value: "Consument", permission: "devices.reportConsumption" },
    { id: "UpdateSetting", label: "Ayar Değişikliği", value: "UpdateSetting", permission: "devices.reportSettings" },
    { id: "UpdateInfo", label: "Bilgi Değişikliği", value: "UpdateInfo", permission: "devices.reportInfo" },
    {
      id: "UpdateManualQuota",
      label: "Manual Kota Yükleme",
      value: "UpdateManualQuota",
      permission: "devices.reportQuota",
    },
    {
      id: "QuotaChange",
      label: "Kota Değişikliği",
      value: "QuotaChange",
      permission: "devices.reportQuota",
    },
    { id: "OperationInfo", label: "Operasyon Bilgisi", value: "OperationInfo", permission: "devices.reportOperation" },
  ];
  const reportTypeOptions = allReportTypeOptions.filter((opt) => hasPermission(opt.permission));

  const filterTypeOptions = [
    { id: "Hourly", label: "Saatlik", value: "Hourly" },
    { id: "Day", label: "Günlük", value: "Day" },
    { id: "Month", label: "Aylık", value: "Month" },
    { id: "Year", label: "Yıllık", value: "Year" },
  ];

  const deviceStatusOptions = [
    { id: "Firma", label: "Firma", value: "Firma" },
    { id: "Servis", label: "Servis", value: "Servis" },
    { id: "Plasiyer", label: "Plasiyer", value: "Plasiyer" },
    { id: "Depo", label: "Depo", value: "Depo" },
    { id: "Üretim", label: "Üretim", value: "Üretim" },
  ];

  const selectedStatusOptions = deviceStatusOptions.filter((opt) =>
    selectedStatuses.includes(opt.value)
  );

  // Operasyon Bilgisi: yüklü veriden operasyon ismi filtre seçenekleri
  const operationNameOptions = useMemo(() => {
    const names = new Set();
    (parsedData || []).forEach((dayData) => {
      (dayData.logs || []).forEach((log) => {
        if (log.operationName) names.add(getOperationDisplayName(log.operationName));
      });
    });
    return [
      { id: "all", label: "Tümü", value: "all" },
      ...[...names]
        .sort((a, b) => a.localeCompare(b, "tr"))
        .map((n) => ({ id: n, label: n, value: n })),
    ];
  }, [parsedData]);

  // Bilgi Değişikliği: yüklü veriden bilgi adı filtre seçenekleri
  const infoNameOptions = useMemo(() => {
    const names = new Set();
    (parsedData || []).forEach((dayData) => {
      (dayData.logs || []).forEach((log) => {
        if (log.infoName) names.add(log.infoName);
      });
    });
    return [
      { id: "all", label: "Tümü", value: "all" },
      ...[...names]
        .sort((a, b) => a.localeCompare(b, "tr"))
        .map((n) => ({ id: n, label: displayLogInfoName(n), value: n })),
    ];
  }, [parsedData]);

  // Cihazın sahip olduğu ürünlerden dinamik ürün listesi
  const productOptions = (selectedDevice?.productInfo?.productInfo || []).map(
    (product) => ({
      id: product.productName,
      label: product.productName,
      value: product.productName,
    })
  );

  // Rapor tipi değişince eski veriyi temizle
  const lastFetchedTypeRef = useRef(null);
  useEffect(() => {
    setParsedData([]);
    lastFetchedTypeRef.current = null;
  }, [reportType]);

  useEffect(() => {
    // Sadece aktif rapor tipi için fetch edilen veriyi kabul et
    if (Datas && lastFetchedTypeRef.current === reportType) {
      setParsedData(Datas);
    }
  }, [Datas, reportType]);

  // handleGetLogData'yı ref ile güncel tut (stale closure önlemi)
  const handleGetLogDataRef = useRef(handleGetLogData);
  useEffect(() => {
    handleGetLogDataRef.current = handleGetLogData;
  });

  // Fetch tetikleyici: tarihler state'e yazıldıktan sonra çalışır
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const pendingFetchRef = useRef(null);

  useEffect(() => {
    if (fetchTrigger > 0 && pendingFetchRef.current !== null) {
      const product = pendingFetchRef.current;
      pendingFetchRef.current = null;
      handleGetLogDataRef.current(product);
    }
  }, [fetchTrigger]);

  const handleSearch = useCallback(() => {
    if (!startDate || !endDate) {
      return;
    }

    // Tüketim raporu için ürün seçimi zorunlu
    if ((reportType === "Consument" || reportType === "ConsumentReport") && !selectedProduct) {
      setProductError(true);
      return;
    }

    // Tüketim (#343): Müşteri Performans raporu bu cihaz için
    if (reportType === "ConsumentReport") {
      setProductError(false);
      detailReport.run({ id: selectedDevice?._id, startDate, endDate, productName: selectedProduct });
      return;
    }

    // Eski rapor verisini temizle ve fetch tipini kaydet
    setParsedData([]);
    lastFetchedTypeRef.current = reportType;
    setProductError(false);

    // Tarihleri ve input'u güncelle
    setInput({ ...input, type: reportType, productName: selectedProduct });
    setDate(startDate);
    setDateSecond(endDate);

    // Fetch'i bir sonraki render'a ertele (tarih state'lerinin güncellenmesini bekle)
    pendingFetchRef.current = selectedProduct || "";
    setTimeout(() => {
      setFetchTrigger((prev) => prev + 1);
    }, 150);
  }, [startDate, endDate, reportType, selectedProduct, input, setInput, setDate, setDateSecond, detailReport, selectedDevice?._id]);

  const handleClearFilters = () => {
    setReportType("ConsumentReport");
    setFilterType("Day");
    setSelectedProduct(selectedDevice?.productInfo?.productInfo?.[0]?.productName || "");
    setSelectedStatuses(["Firma"]);
    setProductError(false);
    setSelectedOperationName("all");
    setSelectedInfoName("all");
    setParsedData([]);
    lastFetchedTypeRef.current = null;
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setStartDate(firstDay);
    setEndDate(today);
  };

  // Reset key for pagination reset in table
  const resetKey = [reportType, parsedData?.length || 0].join("|");

  // Tarih formatlama yardımcı fonksiyonu (9 Ocak 2025 formatında)
  const formatDateTR = (dateStr) => {
    if (!dateStr) return "-";
    let d;
    // Saatli string gelebilir ("09.07.2026 14:30:00") → sadece tarih kısmını al.
    // Aksi halde new Date("09.07.2026 14:30") JS'te MM.DD sanılıp ay/gün ters çevrilir.
    const datePart =
      typeof dateStr === "string" ? dateStr.trim().split(" ")[0] : dateStr;
    // YYYY-MM-DD formatı
    if (typeof datePart === "string" && datePart.includes("-") && datePart.length === 10) {
      const [year, month, day] = datePart.split("-");
      d = new Date(Number(year), Number(month) - 1, Number(day));
    }
    // DD.MM.YYYY formatı (saatli olsa da yukarıda ayrıldı)
    else if (typeof datePart === "string" && datePart.includes(".")) {
      const [day, month, year] = datePart.split(".");
      d = new Date(Number(year), Number(month) - 1, Number(day));
    }
    // Diğer
    else {
      try { d = new Date(dateStr); } catch (e) { return dateStr; }
    }
    if (!d || isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
  };

  // Table columns definition
  const getTableColumns = () => {
    const columns = getBaseTableColumns();
    if (isDealer && reportType === "Consument") {
      return columns.filter((col) => col.key !== "statusChip");
    }
    return columns;
  };

  const getBaseTableColumns = () => {
    switch (reportType) {
      case "Consument":
        // Filtreleme tipine göre kolonları ayarla
        if (filterType === "Hourly") {
          return [
            { key: "date", label: "Tarih", align: "left" },
            { key: "hour", label: "Saat", align: "center" },
            { key: "statusChip", label: "Durum", align: "center" },
            { key: "consumption", label: "Tüketim", align: "center" },
          ];
        } else if (filterType === "Day") {
          return [
            { key: "date", label: "Tarih", align: "left" },
            { key: "statusChip", label: "Durum", align: "center" },
            { key: "totalConsumption", label: "Toplam Tüketim", align: "center" },
          ];
        } else if (filterType === "Month") {
          return [
            { key: "month", label: "Ay", align: "center" },
            { key: "statusChip", label: "Durum", align: "center" },
            {
              key: "totalConsumption",
              label: "Toplam Tüketim",
              align: "center",
            },
            { key: "averageDaily", label: "Günlük Ortalama", align: "center" },
          ];
        } else if (filterType === "Year") {
          return [
            { key: "year", label: "Yıl", align: "center" },
            { key: "statusChip", label: "Durum", align: "center" },
            {
              key: "totalConsumption",
              label: "Toplam Tüketim",
              align: "center",
            },
            { key: "averageMonthly", label: "Aylık Ortalama", align: "center" },
          ];
        }
        return [
          { key: "date", label: "Tarih", align: "left" },
          { key: "hour", label: "Saat", align: "center" },
          { key: "consumption", label: "Tüketim", align: "center" },
        ];
      case "UpdateSetting":
        return [
          { key: "fullName", label: "Kullanıcı", align: "left" },
          { key: "infoName", label: "Bilgi", align: "left", cellClassName: "whitespace-normal min-w-0 max-w-[280px]" },
          { key: "valueFromTo", label: "İlk Değer → Son Değer", align: "center", cellClassName: "whitespace-normal min-w-0 max-w-[200px]" },
          { key: "dateTime", label: "Tarih / Saat", align: "center" },
        ];
      case "UpdateInfo":
        return [
          { key: "name", label: "İsim", align: "left" },
          { key: "lastName", label: "Soyisim", align: "left" },
          { key: "infoName", label: "Bilgi", align: "left", cellClassName: "whitespace-normal min-w-0 max-w-[140px]" },
          { key: "valueFromTo", label: "İlk Değer → Son Değer", align: "center", cellClassName: "whitespace-normal min-w-0 max-w-[200px]" },
          { key: "infoDescription", label: "Açıklama", align: "left", cellClassName: "whitespace-normal min-w-0 max-w-[460px]" },
          { key: "infoNote", label: "Not", align: "left", cellClassName: "whitespace-normal min-w-0 max-w-[240px]" },
          { key: "dateTime", label: "Tarih/Saat", align: "center" },
        ];
      case "QuotaChange":
        return [
          { key: "quotaSource", label: "İşlem", align: "center" },
          { key: "productName", label: "Ürün", align: "left" },
          { key: "quotaDeltaLabel", label: "Değişim", align: "center" },
          { key: "quotaFromLabel", label: "Önceki", align: "center" },
          { key: "quotaToLabel", label: "Sonraki", align: "center" },
          { key: "firmQuotaLabel", label: "Firma Havuzu", align: "center" },
          { key: "infoDescription", label: "Açıklama", align: "left", cellClassName: "whitespace-normal min-w-0 max-w-[420px]" },
          { key: "byName", label: "İşlemi Yapan", align: "left" },
          { key: "dateTime", label: "Tarih/Saat", align: "center" },
        ];
      case "UpdateManualQuota":
        return [
          { key: "name", label: "İsim", align: "left" },
          { key: "lastName", label: "Soyisim", align: "left" },
          { key: "firmName", label: "Firma", align: "left" },
          { key: "productName", label: "Ürün", align: "left" },
          { key: "quota", label: "Yüklenen", align: "center" },
          { key: "alreadyQuota", label: "Varolan", align: "center" },
          { key: "counter", label: "Sayaç", align: "center" },
          { key: "createdInfo", label: "Tarih", align: "center" },
        ];
      case "OperationInfo":
        return [
          {
            key: "operationName",
            label: "Operasyon İsmi",
            width: "w-1/5",
            align: "left",
            cellClassName: "whitespace-normal min-w-0 max-w-[200px]",
          },
          {
            key: "description",
            label: "Açıklama",
            width: "w-2/5",
            align: "left",
            headerClassName: "hidden md:table-cell",
            cellClassName: "hidden md:table-cell whitespace-normal min-w-0 max-w-[320px]",
          },
          {
            key: "dateTimeMobile",
            label: "Tarih / Saat",
            width: "w-1/5",
            align: "center",
            headerClassName: "md:hidden",
            cellClassName: "md:hidden",
          },
          {
            key: "date",
            label: "Tarih",
            width: "w-1/5",
            align: "center",
            headerClassName: "hidden md:table-cell",
            cellClassName: "hidden md:table-cell",
          },
          {
            key: "hour",
            label: "Saat",
            width: "w-1/5",
            align: "center",
            headerClassName: "hidden md:table-cell",
            cellClassName: "hidden md:table-cell",
          },
        ];
      default:
        return [];
    }
  };

  // Render cell function
  const renderTableCell = (item, column, index) => {
    switch (column.key) {
      // DIKKAT: bu switch'in `default` dali dogrudan "-" doner, item[column.key]
      // fallback'i YOKTUR. Yeni kolon eklerken case'ini de eklemek ZORUNLU —
      // aksi halde kolon sessizce "-" gosterir (2026-08-25'te byName boyle kacti).
      case "byName":
        return (
          <span className="text-sm text-gray-700">{item.byName || "-"}</span>
        );
      case "quotaSource":
        return (
          <span className={`px-2 py-0.5 rounded-md text-xs font-SemiBold whitespace-nowrap ${item.quotaSourceColor || "bg-gray-100 text-gray-700"}`}>
            {item.quotaSource}
          </span>
        );
      case "quotaDeltaLabel":
        return (
          <span className={`text-sm font-Bold ${item.quotaDeltaColor || "text-gray-600"}`}>
            {item.quotaDeltaLabel}
          </span>
        );
      case "quotaFromLabel":
        return <span className="text-sm font-Medium text-gray-600">{item.quotaFromLabel}</span>;
      case "quotaToLabel":
        return <span className="text-sm font-Bold text-gray-800">{item.quotaToLabel}</span>;
      case "firmQuotaLabel":
        return <span className="text-xs text-gray-500 whitespace-nowrap">{item.firmQuotaLabel}</span>;
      case "date":
        return (
          <span className="text-sm font-semibold text-gray-800">
            {formatDateTR(item.date)}
          </span>
        );
      case "hour":
        return (
          <span className="text-sm text-gray-600">{item.hour || "-"}</span>
        );
      case "dateTime": {
        const timePart = (item.dateTime || "").split(" ").slice(1).join(" ");
        return (
          <span className="text-sm text-gray-600">
            {item.dateTime ? `${formatDateTR(item.dateTime)}${timePart ? ` ${timePart}` : ""}` : "-"}
          </span>
        );
      }
      case "month":
        return (
          <span className="text-sm font-semibold text-gray-800">
            {item.month || "-"}
          </span>
        );
      case "year":
        return (
          <span className="text-sm font-semibold text-gray-800">
            {item.year || "-"}
          </span>
        );
      case "productName":
        return (
          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
            {item.productName || "-"}
          </span>
        );
      case "consumption":
        return (
          <span className="text-sm font-bold text-green-600">
            {item.consumption || 0}
          </span>
        );
      case "statusChip": {
        const status = item.deviceStatusName || "Bilinmiyor";
        const colors = getStatusColorClasses(status);
        return (
          <span className={`px-2 py-0.5 ${colors.bg} ${colors.text} rounded-full text-xs font-semibold`}>
            {getStatusLocative(status)}
          </span>
        );
      }
      case "totalConsumption":
        return (
          <span className="text-sm font-bold text-green-600">
            {item.totalConsumption || 0}
          </span>
        );
      case "averageHourly":
        return (
          <span className="text-sm font-medium text-teal-600">
            {item.averageHourly ? item.averageHourly.toFixed(2) : "0.00"}
          </span>
        );
      case "averageDaily":
        return (
          <span className="text-sm font-medium text-blue-600">
            {item.averageDaily ? item.averageDaily.toFixed(2) : "0.00"}
          </span>
        );
      case "averageMonthly":
        return (
          <span className="text-sm font-medium text-purple-600">
            {item.averageMonthly ? item.averageMonthly.toFixed(2) : "0.00"}
          </span>
        );
      case "fullName":
        return (
          <span className="text-sm font-semibold text-gray-800">
            {[item.name, item.lastName].filter(Boolean).join(" ") || "-"}
          </span>
        );
      case "name":
        return (
          <span className="text-sm font-semibold text-gray-800">
            {item.name || "-"}
          </span>
        );
      case "lastName":
        return (
          <span className="text-sm text-gray-600">{item.lastName || "-"}</span>
        );
      case "infoName":
        return (
          <span className="text-sm text-gray-700">{item.infoName || "-"}</span>
        );
      case "valueFromTo":
        return (
          <span className="inline-flex items-center gap-2 flex-wrap">
            <span className={item.valueFrom === item.valueTo ? "text-sm font-medium text-gray-700" : "text-sm font-bold text-red-600"}>
              {item.valueFrom ?? "-"}
            </span>
            <FaLongArrowAltRight className="text-gray-400 shrink-0 w-3.5 h-3.5" aria-hidden />
            <span className={item.valueFrom === item.valueTo ? "text-sm font-medium text-gray-700" : "text-sm font-bold text-green-600"}>
              {item.valueTo ?? "-"}
            </span>
          </span>
        );
      case "firmName":
        return (
          <span className="text-sm text-gray-700">{item.firmName || "-"}</span>
        );
      case "quota":
        return (
          <span className="text-sm font-bold text-green-600">
            {item.quota || 0}
          </span>
        );
      case "alreadyQuota":
        return (
          <span className="text-sm font-bold text-amber-600">
            {item.alreadyQuota || 0}
          </span>
        );
      case "counter":
        return (
          <span className="text-sm font-bold text-violet-600">
            {item.counter || 0}
          </span>
        );
      case "operationName": {
        const displayName = getOperationDisplayName(item.operationName);
        return (
          <span
            className="text-sm font-semibold text-gray-800 block truncate min-w-0"
            title={displayName}
          >
            {displayName}
          </span>
        );
      }
      case "dateTimeMobile":
        return (
          <span className="text-sm text-gray-600">
            {item.operationDateTime || (item.date && item.hour ? `${item.date} ${item.hour}` : "-")}
          </span>
        );
      case "description":
        return (
          <span
            className="text-sm text-gray-700 block max-w-xs truncate"
            title={item.description || "-"}
          >
            {item.description || "-"}
          </span>
        );
      case "note":
        return (
          <span
            className="text-sm text-gray-700 block max-w-xs truncate"
            title={item.note || "-"}
          >
            {item.note || "-"}
          </span>
        );
      case "infoDescription":
        return (
          <span
            className="text-sm text-gray-700 block break-words whitespace-normal line-clamp-3"
            title={item.description || "-"}
          >
            {item.description || "-"}
          </span>
        );
      case "infoNote":
        return (
          <span
            className="text-sm text-gray-700 block max-w-xs truncate"
            title={item.note || "-"}
          >
            {item.note || "-"}
          </span>
        );
      case "createdInfo":
        return (
          <span className="text-sm text-gray-600">
            {item.createdInfo || "-"}
          </span>
        );
      default:
        return <span className="text-sm text-gray-600">-</span>;
    }
  };

  // Get table data based on report type
  const getTableData = () => {
    if (!parsedData || parsedData.length === 0) return [];

    if (reportType === "Consument") {
      // Tüm günlerin verilerini birleştir
      const allLogs = [];
      parsedData.forEach((dayData) => {
        if (dayData.logs && Array.isArray(dayData.logs)) {
          const firmArr = dayData.maps?.firms || [];
          dayData.logs.forEach((log) => {
            const fm = firmArr.find((f) => String(f._id) === String(log.firm));
            allLogs.push({
              ...log,
              date: dayData.date, // Gün bilgisini ekle
              // Düzeltme drawer'ında mevcut firma adını gösterebilmek için
              firmName: fm?.firmName || fm?.name || "",
            });
          });
        }
      });
      // Cihaz Durumu filtresi uygula (deviceStatusName varsa)
      // Boş seçim → kullanıcı tüm durumları bilinçli kaldırdı, sonuç boş olmalı
      // Bayide filtre yok — süzme sunucuda yapılır.
      const filteredByStatus = isDealer
        ? allLogs
        : allLogs.filter((log) => {
            if (!log.deviceStatusName) return true;
            return selectedStatuses.includes(log.deviceStatusName);
          });
      return processConsumptionData(filteredByStatus);
    } else {
      // Diğer rapor tipleri için tüm günlerin verilerini birleştir
      const allLogs = [];
      parsedData.forEach((dayData) => {
        if (dayData.logs && Array.isArray(dayData.logs)) {
          dayData.logs.forEach((log) => {
            allLogs.push({
              ...log,
              date: dayData.date, // Gün bilgisini ekle
            });
          });
        }
      });

      // Cihaz Durumu filtresi uygula (deviceStatusName varsa)
      // Boş seçim → kullanıcı tüm durumları bilinçli kaldırdı, sonuç boş olmalı
      const filteredByStatus = allLogs.filter((log) => {
        if (!log.deviceStatusName) return true;
        return selectedStatuses.includes(log.deviceStatusName);
      });

      // Operasyon ismi filtresi (sadece Operasyon Bilgisi raporunda)
      let filteredLogs =
        reportType === "OperationInfo" && selectedOperationName !== "all"
          ? filteredByStatus.filter(
              (log) => getOperationDisplayName(log.operationName) === selectedOperationName
            )
          : filteredByStatus;
      // Bilgi adı filtresi (sadece Bilgi Değişikliği raporunda)
      if (reportType === "UpdateInfo" && selectedInfoName !== "all") {
        filteredLogs = filteredLogs.filter((log) => log.infoName === selectedInfoName);
      }

      const mapped = filteredLogs.map((log, index) => {
        // Ayar değişikliği ve bilgi değişikliği için tarih ve saati ayrı alanlara ayır
        if (reportType === "UpdateSetting" || reportType === "UpdateInfo") {
          const logDate = log.createdInfo ? new Date(log.createdInfo) : null;
          const dateStr = logDate
            ? logDate.toLocaleDateString("tr-TR")
            : "-";
          const timeStr = logDate
            ? logDate.toLocaleTimeString("tr-TR", {
              hour: "2-digit",
              minute: "2-digit",
            })
            : "-";
          // Gösterim kopyası (#148): "Aktiflik" → "Kota Alma"; filtre yukarıda
          // ham değerle yapıldı, originalData ham kalır.
          const shown = displayLogEntry(log);
          return {
            ...shown,
            date: dateStr,
            hour: timeStr,
            dateTime: `${dateStr} ${timeStr}`,
            fullName: [log.name, log.lastName].filter(Boolean).join(" ") || "-",
            settingDate: dateStr, // Excel için
            settingHour: timeStr, // Excel için
            infoDate: dateStr, // Excel için
            infoHour: timeStr, // Excel için
            infoDateTime: `${dateStr} ${timeStr}`, // Excel için
            infoDescription: shown.description || "",
            infoNote: shown.note || "",
            originalData: log,
          };
        }

        // Kota degisim raporu — kolon anlami: quotaFrom/quotaTo MAKINE
        // bakiyesidir; firma havuzu ayri kolonda baglam olarak durur.
        if (reportType === "QuotaChange") {
          const d = new Date(log.createdInfo);
          const dStr = isNaN(d) ? "-" : d.toLocaleDateString("tr-TR");
          const tStr = isNaN(d)
            ? ""
            : d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
          const delta = Number(log.quotaDelta) || 0;
          const has = (v) => v !== null && v !== undefined;
          return {
            ...log,
            quotaSource: DEVICE_QUOTA_SOURCE_LABELS[log.source] || log.source || "-",
            quotaSourceColor:
              DEVICE_QUOTA_SOURCE_COLORS[log.source] || "bg-gray-100 text-gray-700",
            quotaDelta: delta,
            quotaDeltaLabel: `${delta > 0 ? "+" : ""}${delta}`,
            quotaDeltaColor:
              delta > 0 ? "text-green-600" : delta < 0 ? "text-red-600" : "text-gray-600",
            quotaFromLabel: has(log.quotaFrom) ? log.quotaFrom : "-",
            quotaToLabel: has(log.quotaTo) ? log.quotaTo : "-",
            firmQuotaLabel:
              has(log.firmQuotaFrom) && has(log.firmQuotaTo)
                ? `${log.firmQuotaFrom} → ${log.firmQuotaTo}`
                : "-",
            infoDescription: displayLogText(log.description) || "-",
            byName: [log.name, log.lastName].filter(Boolean).join(" ") || "-",
            dateTime: `${dStr} ${tStr}`.trim(),
            originalData: log,
          };
        }

        // Manuel kota raporu için productInfo'yu düzleştir
        if (reportType === "UpdateManualQuota" && log.productInfo) {
          return {
            ...log,
            productName: log.productInfo.productName,
            quota: log.productInfo.quota,
            alreadyQuota: log.productInfo.alreadyQuota,
            counter: log.productInfo.counter,
            originalData: log,
          };
        }

        // Operasyon bilgisi için tarih ve saati ayrı alanlara ayır
        if (reportType === "OperationInfo") {
          const logDate = log.createdInfo ? new Date(log.createdInfo) : null;
          return {
            ...displayLogEntry(log),
            date: logDate ? logDate.toLocaleDateString("tr-TR") : "-",
            hour: logDate
              ? logDate.toLocaleTimeString("tr-TR", {
                hour: "2-digit",
                minute: "2-digit",
              })
              : "-",
            operationDate: logDate ? logDate.toLocaleDateString("tr-TR") : "-", // Excel için
            operationHour: logDate
              ? logDate.toLocaleTimeString("tr-TR", {
                hour: "2-digit",
                minute: "2-digit",
              })
              : "-", // Excel için
            operationDateTime: logDate
              ? `${logDate.toLocaleDateString("tr-TR")} ${logDate.toLocaleTimeString(
                "tr-TR",
                { hour: "2-digit", minute: "2-digit" }
              )}`
              : "-", // Excel için
            originalData: log,
          };
        }

        return {
          ...log,
          originalData: log,
        };
      });
      // Operasyon bilgisi: yeniden eskiye (en yeni en üstte)
      if (reportType === "OperationInfo") {
        mapped.sort((a, b) => new Date(b.createdInfo || 0) - new Date(a.createdInfo || 0));
      }
      return mapped;
    }
  };

  // Process consumption data based on filter type
  const processConsumptionData = (logs) => {
    if (filterType === "Hourly") {
      // Saatlik görünüm - her kayıt ayrı satır
      return logs.map((log, index) => ({
        date: formatDateTR(
          log.date ||
          (log.createdInfo
            ? new Date(log.createdInfo).toLocaleDateString("tr-TR")
            : "-")
        ),
        hour: log.createdInfo
          ? new Date(log.createdInfo).toLocaleTimeString("tr-TR", {
            hour: "2-digit",
            minute: "2-digit",
          })
          : "-",
        productName: log.productName || "-",
        consumption: log.consument || 0,
        isActive: log.isActive,
        deviceStatusName: log.deviceStatusName,
        originalData: log,
      }));
    } else if (filterType === "Day") {
      // Günlük görünüm - (date, status) çiftine göre toplanır
      const dailyData = {};
      logs.forEach((log) => {
        const dateStr = log.date ||
          (log.createdInfo
            ? new Date(log.createdInfo).toLocaleDateString("tr-TR")
            : null);
        if (!dateStr) return;
        const dateKey = formatDateTR(dateStr);
        const statusKey = log.deviceStatusName || "Bilinmiyor";
        const compositeKey = `${dateKey}__${statusKey}`;
        if (!dailyData[compositeKey]) {
          dailyData[compositeKey] = {
            date: dateKey,
            deviceStatusName: statusKey,
            total: 0,
            hourCount: new Set(),
          };
        }
        dailyData[compositeKey].total += log.consument || 0;
        if (log.createdInfo) {
          dailyData[compositeKey].hourCount.add(new Date(log.createdInfo).getHours());
        }
      });

      const parseDateKey = (dStr) => {
        const [d, m, y] = dStr.split(".");
        return new Date(`${y}-${m}-${d}`).getTime();
      };

      return Object.values(dailyData)
        .map((d) => ({
          date: d.date,
          deviceStatusName: d.deviceStatusName,
          totalConsumption: d.total,
          averageHourly: d.hourCount.size > 0 ? d.total / d.hourCount.size : 0,
          originalData: d,
        }))
        .sort((a, b) => {
          const tDiff = parseDateKey(a.date) - parseDateKey(b.date);
          if (tDiff !== 0) return tDiff;
          const ai = STATUS_ORDER.indexOf(a.deviceStatusName);
          const bi = STATUS_ORDER.indexOf(b.deviceStatusName);
          if (ai !== -1 && bi !== -1) return ai - bi;
          if (ai !== -1) return -1;
          if (bi !== -1) return 1;
          return a.deviceStatusName.localeCompare(b.deviceStatusName, "tr");
        });
    } else if (filterType === "Month") {
      // Aylık görünüm - (month, status) çiftine göre toplanır
      const monthlyData = {};
      logs.forEach((log) => {
        if (!log.createdInfo) return;
        const date = new Date(log.createdInfo);
        const monthKey = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}`;
        const monthName = date.toLocaleDateString("tr-TR", {
          year: "numeric",
          month: "long",
        });
        const statusKey = log.deviceStatusName || "Bilinmiyor";
        const compositeKey = `${monthKey}__${statusKey}`;

        if (!monthlyData[compositeKey]) {
          monthlyData[compositeKey] = {
            monthKey,
            month: monthName,
            deviceStatusName: statusKey,
            total: 0,
            totalDays: new Set(),
          };
        }
        monthlyData[compositeKey].totalDays.add(date.getDate());
        monthlyData[compositeKey].total += log.consument || 0;
      });

      return Object.values(monthlyData)
        .map((d) => ({
          month: d.month,
          deviceStatusName: d.deviceStatusName,
          totalConsumption: d.total,
          averageDaily: d.totalDays.size > 0 ? d.total / d.totalDays.size : 0,
          isActive: true,
          originalData: {
            monthKey: d.monthKey,
            deviceStatusName: d.deviceStatusName,
            totalConsumption: d.total,
            averageDaily: d.totalDays.size > 0 ? d.total / d.totalDays.size : 0,
          },
        }))
        .sort((a, b) => {
          const ak = a.originalData.monthKey;
          const bk = b.originalData.monthKey;
          if (ak !== bk) return ak.localeCompare(bk);
          const ai = STATUS_ORDER.indexOf(a.deviceStatusName);
          const bi = STATUS_ORDER.indexOf(b.deviceStatusName);
          if (ai !== -1 && bi !== -1) return ai - bi;
          if (ai !== -1) return -1;
          if (bi !== -1) return 1;
          return a.deviceStatusName.localeCompare(b.deviceStatusName, "tr");
        });
    } else if (filterType === "Year") {
      // Yıllık görünüm - (year, status) çiftine göre toplanır
      const yearlyData = {};
      logs.forEach((log) => {
        if (!log.createdInfo) return;
        const date = new Date(log.createdInfo);
        const yearKey = date.getFullYear().toString();
        const statusKey = log.deviceStatusName || "Bilinmiyor";
        const compositeKey = `${yearKey}__${statusKey}`;

        if (!yearlyData[compositeKey]) {
          yearlyData[compositeKey] = {
            yearKey,
            year: yearKey,
            deviceStatusName: statusKey,
            total: 0,
            totalMonths: new Set(),
          };
        }
        yearlyData[compositeKey].totalMonths.add(date.getMonth());
        yearlyData[compositeKey].total += log.consument || 0;
      });

      return Object.values(yearlyData)
        .map((d) => ({
          year: d.year,
          deviceStatusName: d.deviceStatusName,
          totalConsumption: d.total,
          averageMonthly: d.totalMonths.size > 0 ? d.total / d.totalMonths.size : 0,
          isActive: true,
          originalData: {
            yearKey: d.yearKey,
            deviceStatusName: d.deviceStatusName,
            totalConsumption: d.total,
            averageMonthly: d.totalMonths.size > 0 ? d.total / d.totalMonths.size : 0,
          },
        }))
        .sort((a, b) => {
          if (a.year !== b.year) return a.year.localeCompare(b.year);
          const ai = STATUS_ORDER.indexOf(a.deviceStatusName);
          const bi = STATUS_ORDER.indexOf(b.deviceStatusName);
          if (ai !== -1 && bi !== -1) return ai - bi;
          if (ai !== -1) return -1;
          if (bi !== -1) return 1;
          return a.deviceStatusName.localeCompare(b.deviceStatusName, "tr");
        });
    }

    return logs.map((log, index) => ({
      ...log,
      originalData: log,
    }));
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return `${date.toLocaleDateString("tr-TR")} ${date.toLocaleTimeString(
      "tr-TR",
      { hour: "2-digit", minute: "2-digit" }
    )}`;
  };

  // Adım tipi/alan etiketleri utils/operationMetadata'dan (tek kaynak) gelir.

  const humanizeKey = (value) => {
    if (!value) return "-";
    const withSpaces = String(value)
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2");
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  const getStepLabel = (type) => {
    if (!type) return "Bilgi";
    return STEP_TYPE_LABELS[type] || humanizeKey(type);
  };

  const formatStepValue = (key, value) => {
    if (value == null || value === "") return "-";
    if (typeof value === "boolean") return value ? "Evet" : "Hayır";
    if (typeof value === "number") {
      if (key === "temperature" || key?.toLowerCase().includes("temperature")) {
        return `${value}°C`;
      }
      if (key === "totalWaterTime" || key === "remainingWaterTime") {
        return `${value} sn`;
      }
      return String(value);
    }
    if (value instanceof Date) return formatDateTime(value);
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const getStepKeyLabel = (key, productName) =>
    key === "consument"
      ? getConsumentLabel(productName)
      : STEP_KEY_LABELS[key] || humanizeKey(key);

  const getStepStyle = (type) => {
    const normalizedType = String(type || "").toLowerCase();
    if (
      normalizedType.includes("completed") ||
      normalizedType.includes("success")
    ) {
      return {
        icon: <FaCheckCircle className="text-green-500 w-4 h-4" />,
        bg: "bg-green-50 border-green-200",
        text: "text-green-700",
      };
    }
    if (
      normalizedType.includes("failed") ||
      normalizedType.includes("warning")
    ) {
      return {
        icon: <FaExclamationTriangle className="text-amber-500 w-4 h-4" />,
        bg: "bg-amber-50 border-amber-200",
        text: "text-amber-700",
      };
    }
    switch (type) {
      case "success":
        return {
          icon: <FaCheckCircle className="text-green-500 w-4 h-4" />,
          bg: "bg-green-50 border-green-200",
          text: "text-green-700",
        };
      case "warning":
        return {
          icon: <FaExclamationTriangle className="text-amber-500 w-4 h-4" />,
          bg: "bg-amber-50 border-amber-200",
          text: "text-amber-700",
        };
      default:
        return {
          icon: <FaInfoCircle className="text-blue-500 w-4 h-4" />,
          bg: "bg-blue-50 border-blue-200",
          text: "text-blue-700",
        };
    }
  };

  const renderStepDetails = (step = {}) => {
    const hiddenKeys = new Set(["type", "message", "createdInfo"]);
    const entries = Object.entries(step).filter(
      ([key, value]) => !hiddenKeys.has(key) && value !== undefined
    );
    if (entries.length === 0) return null;
    const stepProductName =
      step.productName || selectedOperationLog?.metadata?.productName;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 text-sm">
        {entries.map(([key, value]) => (
          <div key={key} className="flex flex-col">
            <span className="text-xs text-gray-500">{getStepKeyLabel(key, stepProductName)}</span>
            <span className="text-gray-800 break-words">
              {formatStepValue(key, value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const handleRowClick = (rowData) => {
    // Tüketim raporu (saatlik görünüm): admin tekil kaydı düzeltebilir
    if (reportType === "Consument") {
      if (!isAdmin || filterType !== "Hourly") return;
      const log = rowData?.originalData;
      if (!log?.logId) return;
      setConsumentEdit({ open: true, log });
      return;
    }
    if (reportType !== "OperationInfo") return;
    setSelectedOperationLog(rowData?.originalData || rowData || null);
    setShowOperationDrawer(true);
  };

  // Excel konfigürasyonu
  const excelConfig = {
    sheetName: "Cihaz Raporu",
    columnConfig: {
      widths: [15, 12, 20, 15, 25, 40, 30, 20, 15, 15], // Sütun genişlikleri
      numericColumns: ["consumption", "quota", "alreadyQuota", "counter"],
      columnMap: {
        // Tüketim raporu için
        date: 0,
        hour: 1,
        productName: 2,
        consumption: 3,
        status: 4,
        // Ayar değişikliği raporu için
        name: 0,
        lastName: 1,
        infoName: 2,
        valueFromTo: 3,
        settingDate: 4, // Ayar değişikliği için tarih
        settingHour: 5, // Ayar değişikliği için saat
        // Bilgi değişikliği raporu için
        infoDescription: 5,
        infoNote: 6,
        infoDateTime: 7,
        // Manuel kota raporu için
        firmName: 2,
        quota: 4,
        alreadyQuota: 5,
        counter: 6,
        createdInfo: 7,
        // Operasyon bilgisi raporu için
        operationName: 0,
        description: 1,
        operationDateTime: 2, // Operasyon bilgisi için tarih/saat
      },
    },
    headerStyle: {
      font: { bold: true, color: { rgb: "FFFFFF" }, size: 12 },
      fill: { fgColor: { rgb: "667eea" } },
      alignment: { horizontal: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    },
    dataStyle: {
      font: { size: 11 },
      alignment: { horizontal: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "E0E0E0" } },
        bottom: { style: "thin", color: { rgb: "E0E0E0" } },
        left: { style: "thin", color: { rgb: "E0E0E0" } },
        right: { style: "thin", color: { rgb: "E0E0E0" } },
      },
    },
    alternateRowStyle: {
      fill: { fgColor: { rgb: "F8F9FA" } },
    },
  };

  // Dosya adı oluşturma
  const filterLabel = filterTypeOptions.find((o) => o.value === filterType)?.label || "";
  const reportLabel = reportTypeOptions.find((o) => o.value === reportType)?.label || "Rapor";
  const productLabel = reportType === "Consument" && selectedProduct ? `_${selectedProduct}` : "";
  const fileName = `${reportLabel}${productLabel}_${filterLabel}_${startDate ? startDate.toISOString().split("T")[0] : ""
    }_${endDate ? endDate.toISOString().split("T")[0] : ""}.xlsx`;

  // Excel verilerini hazırlama
  const normalizeExcelText = (value) => {
    if (!value) return "-";
    const normalized = String(value).replace(/<br\s*\/?>/gi, "\n");
    const lines = normalized.split(/\r?\n/);
    if (lines.length <= 4) return normalized;
    return [...lines.slice(0, 4), "…"].join("\n");
  };

  const getExcelData = () => {
    const tableDataAll = getTableData();

    if (reportType === "Consument") {
      const valueOf = (item) => filterType === "Hourly"
        ? (item.consumption || 0)
        : (item.totalConsumption || 0);

      const rows = tableDataAll.map((item) => {
        const excelItem = {};
        // Ürün adını ilk sütun olarak ekle
        excelItem["Ürün"] = selectedProduct || "-";
        getTableColumns().forEach((column) => {
          if (column.key === "statusChip") {
            excelItem[column.label] = getStatusLocative(item.deviceStatusName);
            return;
          }
          const val = item[column.key];
          if (column.key === "averageHourly" || column.key === "averageDaily" || column.key === "averageMonthly") {
            excelItem[column.label] = val ? Number(val).toFixed(2) : "0.00";
          } else {
            excelItem[column.label] = val ?? "-";
          }
        });
        return excelItem;
      });

      // Durum bazlı ara toplamlar
      const totalsByStatus = new Map();
      tableDataAll.forEach((item) => {
        const status = item.deviceStatusName || "Bilinmiyor";
        totalsByStatus.set(status, (totalsByStatus.get(status) || 0) + valueOf(item));
      });

      const sortedStatuses = sortStatuses(Array.from(totalsByStatus.keys()));
      const consumptionColKey = filterType === "Hourly" ? "consumption" : "totalConsumption";

      sortedStatuses.forEach((status) => {
        const subRow = { "Ürün": "-" };
        getTableColumns().forEach((column, index) => {
          if (index === 0) {
            subRow[column.label] = `${getStatusLocative(status)} Ara Toplam`;
            return;
          }
          if (column.key === "statusChip") {
            subRow[column.label] = getStatusLocative(status);
            return;
          }
          if (column.key === consumptionColKey) {
            subRow[column.label] = totalsByStatus.get(status);
            return;
          }
          subRow[column.label] = "-";
        });
        rows.push(subRow);
      });

      // Genel toplam
      const grandTotal = Array.from(totalsByStatus.values()).reduce((a, b) => a + b, 0);
      const totalRow = { "Ürün": "-" };
      getTableColumns().forEach((column, index) => {
        if (index === 0) {
          totalRow[column.label] = "Genel Toplam";
          return;
        }
        if (column.key === consumptionColKey) {
          totalRow[column.label] = grandTotal;
          return;
        }
        totalRow[column.label] = "-";
      });
      rows.push(totalRow);

      return rows;
    }

    return tableDataAll.map((item) => {
      if (reportType === "OperationInfo") {
        return {
          "Operasyon İsmi": item.operationName || "-",
          Açıklama: item.description || "-",
          "Tarih/Saat": item.operationDateTime || "-",
        };
      }
      const excelItem = {};
      getTableColumns().forEach((column) => {
        if (column.key === "infoDescription" || column.key === "infoNote") {
          excelItem[column.label] = normalizeExcelText(item[column.key]);
          return;
        }
        if (column.key === "valueFromTo") {
          excelItem[column.label] = `${item.valueFrom ?? "-"} → ${item.valueTo ?? "-"}`;
          return;
        }
        excelItem[column.label] = item[column.key] || "-";
      });
      return excelItem;
    });
  };


  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
      <div className="flex flex-col h-full">
        {/* Tüketim kaydı düzeltme drawer'ı — sadece admin, saatlik satıra tıklayınca */}
        <ConsumentEditDrawer
          open={consumentEdit.open}
          onClose={() => setConsumentEdit({ open: false, log: null })}
          log={consumentEdit.log}
          onSuccess={handleSearch}
        />

        {/* Operasyon Drawer */}
        {opDrawerMounted && (
          <div className="fixed inset-0 z-50">
            <div
              className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ease-in-out ${opDrawerEntered ? "opacity-100" : "opacity-0"}`}
              onClick={() => setShowOperationDrawer(false)}
            />
            <div
              className={`absolute right-0 top-0 h-screen w-full sm:w-[600px] bg-white shadow-2xl border-l border-gray-200 flex flex-col transform ${opDrawerEntered ? "translate-x-0" : "translate-x-full"} transition-transform duration-300 ease-in-out`}
            >
              {/* Sabit header */}
              <div className="shrink-0 bg-indigo-600 text-white px-5 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="bg-white/10 rounded-lg p-2">
                      <FaCogs className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold">Operasyon Detayı</h3>
                      <div className="text-xs text-indigo-200">
                        {getOperationDisplayName(selectedOperationLog?.operationName)}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowOperationDrawer(false)}
                    className="text-white/80 hover:text-white transition-colors p-1"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              {/* Sabit: Zaman, Açıklama, Metadata */}
              <div className="shrink-0 p-5 space-y-3 border-b border-gray-100 overflow-hidden">
                {/* Açıklama + Zaman yan yana (mobilde Açıklama üstte) */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="rounded-xl border border-gray-200 p-3 flex-1 min-w-0">
                    <h4 className="text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1.5">
                      <FaInfoCircle className="w-3 h-3 text-indigo-500" /> Açıklama
                    </h4>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap">{displayLogText(selectedOperationLog?.description) || "-"}</div>
                  </div>
                  <div className="rounded-xl border border-gray-200 p-3 shrink-0 sm:min-w-[160px]">
                    <h4 className="text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1.5">
                      <FaClock className="w-3 h-3 text-blue-500" /> Zaman
                    </h4>
                    <div className="text-sm text-gray-800">{formatDateTime(selectedOperationLog?.createdInfo)}</div>
                  </div>
                </div>
                {selectedOperationLog?.metadata != null && typeof selectedOperationLog.metadata === "object" && Object.keys(selectedOperationLog.metadata).length > 0 && !isOperationHidingMeta(selectedOperationLog?.operationName) && (
                  <div className="rounded-xl border border-gray-200 p-3">
                    <h4 className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1.5">
                      <FaInfoCircle className="w-3 h-3 text-amber-500" /> Bilgiler
                    </h4>
                    {(() => {
                      const meta = selectedOperationLog.metadata;
                      const entries = Object.entries(meta).filter(
                        ([key]) => !METADATA_HIDDEN_KEYS.includes(key)
                      );
                      const pairedKeys = new Set(METADATA_PAIR_GROUPS.flat());
                      const restEntries = sortMetadataEntries(
                        entries.filter(([key]) => !pairedKeys.has(key))
                      );
                      const hasKey = (k) => entries.some(([key]) => key === k);
                      // Etiketler ortak util'den: burada eskiden ham key'e
                      // düşülüyordu (METADATA_KEY_LABELS[key] ?? key), bu yüzden
                      // sözlükte olmayan alanlar İngilizce görünüyordu.
                      // getMetadataLabel ayrıca potIndex'i ürüne göre
                      // Göz/Demlik olarak çevirir.
                      const labelFor = (key) =>
                        getMetadataLabel(
                          key,
                          meta.productName,
                          selectedOperationLog?.operationName
                        );
                      const formatMetaValue = (key, value) => {
                        let displayValue = value;
                        if (typeof value === "object" && value !== null) {
                          displayValue = JSON.stringify(value);
                        } else if (typeof value === "boolean") {
                          displayValue = value ? "Evet" : "Hayır";
                        } else if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
                          displayValue = formatDateTime(value);
                        } else {
                          displayValue = value != null ? String(value) : "-";
                        }
                        if (
                          (key === "totalWaterTime" || key === "remainingWaterTime") &&
                          displayValue !== "-"
                        ) {
                          displayValue = `${displayValue} sn`;
                        }
                        return displayValue;
                      };
                      return (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                          {/* Çiftli tüketim box'ları */}
                          {METADATA_PAIR_GROUPS.filter((group) => group.some(hasKey)).map((group) => (
                            <div
                              key={group.join("-")}
                              className="rounded-lg border bg-emerald-50/60 border-emerald-100 flex divide-x divide-emerald-100 min-w-0"
                            >
                              {group.filter(hasKey).map((key) => (
                                <div key={key} className="flex-1 min-w-0 p-2">
                                  <div className="text-[11px] leading-tight text-emerald-700">{labelFor(key)}</div>
                                  <div className="text-sm font-semibold text-gray-800 break-words">
                                    {formatMetaValue(key, meta[key])}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                          {/* Diğer meta alanları */}
                          {restEntries.map(([key, value]) => (
                            <div key={key} className="rounded-lg border bg-gray-50 border-gray-100 p-2 min-w-0">
                              <div className="text-[11px] leading-tight text-gray-500">{labelFor(key)}</div>
                              <div className="text-sm font-semibold text-gray-800 break-words">
                                {formatMetaValue(key, value)}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              {/* Sadece Operasyon Adımları kayar */}
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <div className="shrink-0 px-5 pt-3 pb-1">
                  <h4 className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                    <FaCogs className="w-3 h-3 text-teal-500" /> Operasyon Adımları
                  </h4>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5">
                  {Array.isArray(selectedOperationLog?.operationSteps) && selectedOperationLog.operationSteps.length > 0 ? (
                    <div className="space-y-2">
                      {selectedOperationLog.operationSteps.map((step, index) => {
                        const style = getStepStyle(step?.type);
                        return (
                          <div key={index} className={`border rounded-lg p-2.5 ${style.bg}`}>
                            <div className="flex items-center gap-2">
                              {style.icon}
                              <span className={`text-xs font-medium ${style.text}`}>{getStepLabel(step?.type)}</span>
                              <span className="ml-auto text-[10px] text-gray-500 shrink-0">{formatDateTime(step?.createdInfo)}</span>
                            </div>
                            {step?.message && <div className="mt-1.5 text-xs text-gray-700">{step.message}</div>}
                            {renderStepDetails(step)}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400">Adım bulunamadı.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Compact Filter Bar */}
        <div className="shrink-0 border-b border-gray-200 bg-gray-50/80 px-3 py-2.5 sm:px-4 sm:py-4">
          {/* Filtre grid */}
          <div className={`grid gap-3 items-end ${reportType === "Consument"
            ? `grid-cols-2 sm:grid-cols-3 ${isDealer ? "lg:grid-cols-5" : "lg:grid-cols-6"}`
            : "grid-cols-2 sm:grid-cols-3"
            }`}>
            <DateRangeField
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              size="sm"
              disablePortal
              className="contents"
              minDate={minReportDate}
            />

            {/* Rapor Tipi */}
            <CustomDropdown
              label="Rapor Tipi"
              compact={true}
              value={reportTypeOptions.find((o) => o.value === reportType)}
              onChange={(v) => {
                setReportType(v.value);
                setParsedData([]);
                setSelectedOperationName("all");
                setSelectedInfoName("all");
                if (v.value !== "Consument" && v.value !== "ConsumentReport") { setSelectedProduct(""); setProductError(false); }
              }}
              options={reportTypeOptions}
              placeholder="Tip seçin"
              showSearch={false}
            />

            {/* Operasyon Bilgisi: operasyon ismine göre filtre */}
            {reportType === "OperationInfo" && (
              <CustomDropdown
                label="Operasyon"
                compact={true}
                value={
                  operationNameOptions.find((o) => o.value === selectedOperationName) ||
                  operationNameOptions[0]
                }
                onChange={(v) => setSelectedOperationName(v.value)}
                options={operationNameOptions}
                placeholder="Operasyon seçin"
                showSearch={false}
              />
            )}

            {/* Bilgi Değişikliği: bilgi adına göre filtre */}
            {reportType === "UpdateInfo" && (
              <CustomDropdown
                label="Bilgi"
                compact={true}
                value={
                  infoNameOptions.find((o) => o.value === selectedInfoName) ||
                  infoNameOptions[0]
                }
                onChange={(v) => setSelectedInfoName(v.value)}
                options={infoNameOptions}
                placeholder="Bilgi seçin"
                showSearch={false}
              />
            )}

            {/* Tüketim (#343): yalnız ürün */}
            {isDetailReport && (
              <CustomDropdown
                label="Ürün"
                required={true}
                compact={true}
                value={productOptions.find((o) => o.value === selectedProduct)}
                onChange={(v) => { setSelectedProduct(v.value); setProductError(false); }}
                options={productOptions}
                placeholder="Ürün seçin"
                error={productError}
                showSearch={false}
              />
            )}

            {/* Tüketim Detayı ek filtreleri */}
            {reportType === "Consument" && (
              <>
                <CustomDropdown
                  label="Filtreleme"
                  compact={true}
                  value={filterTypeOptions.find((o) => o.value === filterType)}
                  onChange={(v) => setFilterType(v.value)}
                  options={filterTypeOptions}
                  placeholder="Filtre"
                  showSearch={false}
                />
                {!isDealer && (
                  <CustomDropdown
                    label="Durum"
                    compact={true}
                    multi={true}
                    value={selectedStatusOptions}
                    onChange={(arr) => setSelectedStatuses(arr.map((o) => o.value))}
                    options={deviceStatusOptions}
                    placeholder="Durum seçin"
                    showSearch={false}
                  />
                )}
                <CustomDropdown
                  label="Ürün"
                  required={true}
                  compact={true}
                  value={productOptions.find((o) => o.value === selectedProduct)}
                  onChange={(v) => { setSelectedProduct(v.value); setProductError(false); }}
                  options={productOptions}
                  placeholder="Ürün seçin"
                  error={productError}
                  showSearch={false}
                />
              </>
            )}
          </div>

          {/* Aksiyon satırı */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200/60">
            <span className="hidden sm:inline text-sm text-gray-400">
              {getTableData().length > 0
                ? <><span className="font-semibold text-gray-600">{getTableData().length}</span> kayıt bulundu</>
                : "Filtreleri seçip raporu getirin"
              }
            </span>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                onClick={handleClearFilters}
                className="flex items-center justify-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:text-gray-800 transition-colors flex-1 sm:flex-none"
              >
                <FaUndoAlt className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span>Temizle</span>
              </button>
              <button
                onClick={handleSearch}
                disabled={isLoading || detailReport.isLoading || !startDate || !endDate || !reportTypeOptions.some((o) => o.value === reportType) || ((reportType === "Consument" || isDetailReport) && !selectedProduct)}
                className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors shadow-sm disabled:bg-gray-300 disabled:shadow-none disabled:cursor-not-allowed flex-1 sm:flex-none"
              >
                {isLoading ? (
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FaSearch className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                )}
                <span>Raporu Getir</span>
              </button>
              {!isDetailReport && <ExportExcel
                excelData={getExcelData()}
                fileName={fileName}
                {...excelConfig}
                renderTrigger={(exportFn) => (
                  <button
                    onClick={exportFn}
                    disabled={getTableData().length === 0}
                    className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-colors shadow-sm disabled:bg-gray-300 disabled:shadow-none disabled:cursor-not-allowed flex-1 sm:flex-none"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Excel İndir</span>
                  </button>
                )}
              />}
            </div>
          </div>
        </div>

        {/* Tüketim (#343): Müşteri Performans raporu — önizleme önce Excel */}
        {isDetailReport && (
          <div className="flex-1 min-h-0 flex flex-col p-3 sm:p-4">
            <PerformanceReportView
              report={detailReport.report}
              isLoading={detailReport.isLoading}
              error={detailReport.error}
            />
          </div>
        )}

        {/* Content Area - Kalan alan (Tüketim raporunda gizli) */}
        <div className={`flex-1 min-h-0 flex flex-col ${isDetailReport ? "hidden" : ""}`}>
          {/* Loading - Kompakt skeleton */}
          {isLoading && (
            <div className="flex-1 flex flex-col">
              {/* Skeleton table header */}
              <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                {getTableColumns().map((col, i) => (
                  <div key={i} className={`h-3 bg-gray-200 rounded animate-pulse ${col.align === "left" ? "flex-[2]" : "flex-1"}`} />
                ))}
              </div>
              {/* Skeleton rows */}
              <div className="flex-1 px-4 py-2">
                {Array.from({ length: 8 }).map((_, idx) => (
                  <div key={idx} className="flex items-center gap-3 py-3 border-b border-gray-50">
                    {getTableColumns().map((col, i) => (
                      <div
                        key={i}
                        className={`h-4 bg-gray-100 rounded animate-pulse ${col.align === "left" ? "flex-[2]" : "flex-1"}`}
                        style={{ animationDelay: `${idx * 80 + i * 40}ms` }}
                      />
                    ))}
                  </div>
                ))}
              </div>
              {/* Skeleton pagination */}
              <div className="shrink-0 flex items-center justify-between px-4 py-2.5 border-t border-gray-100">
                <div className="h-3 w-28 bg-gray-200 rounded animate-pulse" />
                <div className="flex items-center gap-1.5">
                  <div className="h-6 w-14 bg-gray-200 rounded-lg animate-pulse" />
                  <div className="h-6 w-6 bg-gray-200 rounded-lg animate-pulse" />
                  <div className="h-6 w-14 bg-gray-200 rounded-lg animate-pulse" />
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center px-4">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-50 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700">Rapor yüklenirken hata oluştu</p>
                <p className="text-xs text-gray-400 mt-1">{error.message || "Lütfen tekrar deneyin."}</p>
              </div>
            </div>
          )}

          {/* Table */}
          {!isLoading && !error && getTableData().length > 0 && (
            <div className="flex-1 min-h-0 flex flex-col">
              <ReportTable
                title="Cihaz Raporu"
                count={getTableData().length}
                countText="kayıt"
                columns={getTableColumns()}
                data={getTableData()}
                renderCell={renderTableCell}
                onRowClick={handleRowClick}
                emptyMessage="Rapor verisi bulunamadı."
                paginate={true}
                defaultPageSize={25}
                pageSizeOptions={[10, 25, 50, 100]}
                resetKey={resetKey}
                compact={true}
                showHeader={false}
                className="flex-1"
              />
            </div>
          )}

          {/* Empty */}
          {!isLoading && !error && getTableData().length === 0 && (
            <div className="flex-1 flex items-center justify-center select-none">
              <div className="text-center px-4">
                <img
                  src={mascotNoData}
                  alt="Veri bulunamadı"
                  className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 object-contain mx-auto mb-4 drop-shadow-lg"
                  draggable={false}
                />
                <p className="text-base sm:text-lg font-bold text-gray-700">Veri bulunamadı</p>
                <p className="text-sm text-gray-400 mt-1">Filtreleri değiştirip tekrar deneyin.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </LocalizationProvider>
  );
};

export default ReportTab;
